import { Component, OnInit, ViewChild } from '@angular/core';
import { RequestService } from '../../../../services/request.service';
import { SnackService } from '../../../../services/snack.service';
import { FormatDecimal } from '../../../../pipes/format-decimal';
import Materials from 'src/app/model/material/materials';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import { ModalService } from '../../../../services/modal.service';
import { SignSize } from '../../../../model/product/board/sign-size';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SignSizeEditComponent } from './sign-size-edit.component';
import { cloneDeep } from 'lodash';
import { FilterEnabledComponent } from 'src/app/helpers/filter-enabled.component';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { PRODUCTS } from '../../../../helpers/constants';

@Component({
  selector: 'app-sign-size-detail',
  templateUrl: './sign-size-detail.component.html',
  styleUrls: ['./sign-size-detail.component.scss'],
  providers: [FormatDecimal],
})
export class SignSizeDetailComponent
  extends FilterEnabledComponent
  implements OnInit
{
  readonly baseUrl: string = '/sign_sizes';
  productSizes: SignSize[] = null;
  productMaterials: Materials;
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    private modalService: ModalService,
    private dialog: MatDialog,
    protected pageIndexService: PageIndexService,
    protected filterStoreService: FilterStoreService
  ) {
    super(
      [
        {
          key: 'height',
          active: false,
          strictEquality: true,
          numeric: true,
        },
        {
          key: 'width',
          active: false,
          strictEquality: true,
          numeric: true,
        },
      ],
      filterStoreService,
      pageIndexService
    );
  }

  ngOnInit() {
    this.paginator.pageIndex =
      this.initPageIndex == null ? 0 : this.initPageIndex;
    this.paginator.pageSize =
      this.initPageSize == null ? 30 : this.initPageSize;
    this.addSubscription(
      merge(this.paginator.page, this.filtersChanged).pipe(
        startWith({}),
        switchMap(() => {
          this.pageIndexService.setPageIndex(
            this.getLocalStorageKey(),
            this.paginator.pageIndex
          );
          this.pageIndexService.setPageSize(
            this.getLocalStorageKey(),
            this.paginator.pageSize
          );
          return this.requestService.get(
            this.baseUrl,
            null,
            this.getProductsSort(),
            this.getProductsOrder(),
            this.paginator.pageSize,
            this.paginator.pageIndex,
            this.generateFilterString(),
            [{ key: 'width', order: 'asc' }]
          );
        }),
        map((data) => {
          this.totalItems = Number.parseInt(data.headers.get('X-Total-Count'));
          return data.body;
        }),
        catchError(() => {
          return observableOf([]);
        })
      ),
      (data) => {
        this.productSizes = data;
        if (
          !CollectionHelper.isPopulated(data) &&
          this.paginator.pageIndex > 0
        ) {
          this.paginator.pageIndex = 0;
          this.filtersChanged.emit();
        }
      }
    );
  }

  getProductsSort() {
    return 'height';
  }

  getProductsOrder() {
    return 'asc';
  }

  delete(productSize: SignSize, index: number): void {
    this.modalService.confirmDialog(
      'Bord verwijderen',
      'Bent u zeker dat u dit bord wilt verwijderen?',
      (answer) => {
        if (!answer) {
          return;
        }
        if (productSize.id == null) {
          this.productSizes.splice(index, 1);
          this.snackService.showSuccess('Bord succesvol verwijderd');
        } else {
          this.requestService
            .delete(this.baseUrl + '/archive/' + productSize.id)
            .toPromise()
            .then(() => {
              //This should refetch instead, see comment 'addProductSize'
              this.productSizes.splice(index, 1);
              this.snackService.showSuccess('Bord succesvol verwijderd');
            });
        }
      }
    );
  }

  addProductSize(): void {
    const newSize: SignSize = new SignSize();

    this.openSignSizeEditModal(newSize, (sign: SignSize) => {
      this.productSizes.push(sign);
      //This should just re trigger fetch, not push. But fetching is done by subscription
      //so I'm considering this out of scope until we properly implement request services
    });
  }

  editProductSize(productSize: SignSize, index: number): void {
    const productSizeCopy = cloneDeep(productSize);
    this.openSignSizeEditModal(productSizeCopy, (sign: SignSize) => {
      this.productSizes[index] = sign;
    });
  }

  openSignSizeEditModal(
    productSize: SignSize,
    successHandler: (sign: SignSize) => void
  ) {
    const modalRef = this.dialog.open(SignSizeEditComponent, {
      data: {
        productSize: productSize,
      },
    });

    //When implementing this generically, will need to make sure the subscription is also closed when the modal is closed without calling success
    this.addSubscription(
      modalRef.componentInstance.saveSuccessEvent,
      (sign: SignSize) => {
        successHandler(sign);
        modalRef.close();
      }
    );
  }

  duplicate(productSize: SignSize): void {
    const cloned: SignSize = cloneDeep(productSize);
    cloned.id = null;
    cloned.uploadedAssetUuids = [];
    cloned.requiredConstructionMaterials.forEach((i) => (i.id = null));

    this.openSignSizeEditModal(cloned, (sign: SignSize) => {
      this.productSizes.push(sign);
      //Comment: same as 'add'
    });
  }

  protected getLocalStorageKey(): string {
    return 'SignSizeDetailComponent';
  }

  protected readonly PRODUCTS = PRODUCTS;
}
